import { Injectable, inject } from '@angular/core';
import { Observable, map, catchError, of, switchMap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import type { DropdownOption } from '@ui';
import { OnboardingApiService } from '@shared/services/onboarding-api.service';
import type {
  KycOccupationRequest, CodeTextRef, EmployerBusinessOption
} from '@shared/interfaces/opportunity.interface';
import type { HolderContactData, HolderActivityData } from '@shared/interfaces/onboarding-state.interface';
import type { PlaceDetails } from '@shared/services/google-places.service';
import { HOLDER_API } from '@constants';
import { isNullOrUndefined, valueOrEmpty, valueOrNull, valueWhen } from '@utils/predicates';
import { translateOption } from '@utils/translate-option';

export interface ActivityFormValues {
  employmentType: string | null;
  sector: string | null;
  profession: string | null;
  employer: string | null;
  addressSearch: string | null;
  cityText: string | null;
  premise: string | null;
  streetName: string | null;
  houseNumber: string | null;
  floor: string | null;
  postalCode: string | null;
  province: string | null;
  country: string | null;
  countryJustification: string | null;
}

export interface ActivitySubmitParams {
  formValues: ActivityFormValues;
  contactData: HolderContactData;
  holderNumber: typeof HOLDER_API[keyof typeof HOLDER_API];
  selectedCountryCode: string;
  selectedCountryName: string;
  placeDetails: PlaceDetails | null;
  employmentOptions: DropdownOption[];
  sectorOptions: DropdownOption[];
  professionOptions: DropdownOption[];
  provinceOptions: DropdownOption[];
}

interface LoadResult {
  options: DropdownOption[];
  preselectId: string | null;
}

const EMPTY_LOAD_RESULT: LoadResult = { options: [], preselectId: null };

function findOption(options: DropdownOption[], id: string | null | undefined): DropdownOption | undefined {
  if (!id) {
    return undefined;
  }
  return options.find((o) => o.id === id);
}

function toNullIfEmpty(value: string | null | undefined): string | null {
  if (!value || value.trim().length === 0) {
    return null;
  }
  return value;
}

function buildRoute(
  premise?: string | null, streetName?: string | null,
  houseNumber?: string | null, province?: string | null): string {
  return [premise, streetName, houseNumber, province].filter(Boolean).join(' ');
}

function toCodeTextRef(option: DropdownOption | undefined): CodeTextRef | null {
  if (isNullOrUndefined(option)) {
    return null;
  }
  return { code: valueOrEmpty(option.code), text: valueOrEmpty(option.name) };
}

@Injectable({ providedIn: 'root' })
export class ActivitySubmitService {
  private readonly onboardingApi = inject(OnboardingApiService);
  private readonly translate = inject(TranslateService);

  validateStages(oppId: string, holder: string): Observable<boolean> {
    const request = { oppId, holder };
    return this.onboardingApi.validateClient(request).pipe(
      switchMap((response) => {
        if (response.status === 'Rejected') {
          return of(true);
        }
        return this.onboardingApi.validateSsn(request).pipe(
          map((ssnResponse) => ssnResponse.status === 'Rejected')
        );
      }),
      catchError(() => of(false))
    );
  }

  submit(params: ActivitySubmitParams): Observable<HolderActivityData> {
    const { formValues, contactData, holderNumber, selectedCountryCode, selectedCountryName,
      placeDetails, employmentOptions, sectorOptions, professionOptions, provinceOptions } = params;

    const employment = findOption(employmentOptions, formValues.employmentType);
    const sector = findOption(sectorOptions, formValues.sector);
    const profession = findOption(professionOptions, formValues.profession);
    const province = findOption(provinceOptions, formValues.province);

    const hasAddress = !!formValues.cityText || !!formValues.streetName;
    const ishouseNumber = formValues.houseNumber ? `# ${formValues.houseNumber}` : null;

    const request: KycOccupationRequest = {
      cuid: contactData.cuid,
      holder: holderNumber,
      oppId: contactData.opportunityId,
      occupation: toCodeTextRef(profession),
      employmentStatus: toCodeTextRef(employment),
      employerBusiness: toCodeTextRef(sector),
      employer: toNullIfEmpty(formValues.employer),
      employerLatitude: valueOrNull(placeDetails?.latitude),
      employerLongitude: valueOrNull(placeDetails?.longitude),
      employerState: valueWhen(hasAddress, toCodeTextRef(province)),
      employerCity: valueWhen(hasAddress, {
        code: valueOrEmpty(province?.code),
        text: valueOrEmpty(formValues.cityText)
      }),
      employerCountry: valueWhen(hasAddress, {
        code: selectedCountryCode,
        text: selectedCountryName
      }),
      employerStreet: valueWhen(hasAddress, buildRoute(formValues.premise, formValues.streetName, ishouseNumber, formValues.province)),
      employerStreetNumber: toNullIfEmpty(formValues.houseNumber),
      employerBuildingName: toNullIfEmpty(formValues.premise),
      employerApartment: toNullIfEmpty(formValues.floor),
      employerRoute: valueWhen(hasAddress, buildRoute(formValues.premise, formValues.streetName)),
      employerZipCode: toNullIfEmpty(formValues.postalCode)
    };

    if (formValues.countryJustification) {
      request.employAddressExplanation = formValues.countryJustification;
    }

    return this.onboardingApi.sendKycOccupation(request).pipe(
      map(() => this.buildHolderData(formValues, selectedCountryCode, placeDetails,
        employmentOptions, sectorOptions, professionOptions, provinceOptions))
    );
  }

  loadOccupations(): Observable<{
    employmentOptions: DropdownOption[];
    sectorOptions: DropdownOption[];
    sectorRaw: EmployerBusinessOption[];
  }> {
    return this.onboardingApi.getOccupations().pipe(
      map((response) => {
        const employmentOptions: DropdownOption[] = (response.EMPLOYMENT_STATUS ?? []).map((s) => ({
          id: s.code,
          name: this.translate.instant(valueOrEmpty(s.value?.key)),
          code: s.code
        }));

        const sectorRaw = response.EMPLOYER_BUSINESS ?? [];
        const sectorOptions: DropdownOption[] = sectorRaw.map((s) => ({
          id: s.code,
          name: this.translate.instant(valueOrEmpty(s.value?.key)),
          code: s.code
        }));

        return { employmentOptions, sectorOptions, sectorRaw };
      }),
      catchError(() => {
        const empty: DropdownOption[] = [];
        const emptyRaw: EmployerBusinessOption[] = [];
        return of({ employmentOptions: empty, sectorOptions: empty, sectorRaw: emptyRaw });
      })
    );
  }

  buildProfessionOptions(sectorRaw: EmployerBusinessOption[], sectorCode: string): DropdownOption[] {
    const sector = sectorRaw.find((s) => s.code === sectorCode);
    if (isNullOrUndefined(sector) || !sector.children) {
      return [];
    }
    return sector.children.map((p) => ({
      id: p.code,
      name: this.translate.instant(valueOrEmpty(p.value?.key)),
      code: p.code
    }));
  }

  loadProvinces(countryCode: string, preselect?: string): Observable<LoadResult> {
    return this.onboardingApi.getStates(countryCode).pipe(
      map((states) => {
        const options: DropdownOption[] = states.map((s) => ({
          id: translateOption(s.name, this.translate),
          name: translateOption(s.name, this.translate),
          code: s.code
        }));
        let preselectId: string | null = null;
        if (preselect) {
          const matched = options.find((o) => o.name?.toLowerCase() === preselect.toLowerCase());
          if (matched) {
            preselectId = matched.id;
          }
        }
        return { options, preselectId };
      }),
      catchError(() => of(EMPTY_LOAD_RESULT))
    );
  }

  private buildHolderData(
    fv: ActivityFormValues,
    countryCode: string,
    placeDetails: PlaceDetails | null,
    employmentOptions: DropdownOption[],
    sectorOptions: DropdownOption[],
    professionOptions: DropdownOption[],
    provinceOptions: DropdownOption[]
  ): HolderActivityData {
    const employment = findOption(employmentOptions, fv.employmentType);
    const sector = findOption(sectorOptions, fv.sector);
    const profession = findOption(professionOptions, fv.profession);
    const province = findOption(provinceOptions, fv.province);

    return {
      employmentStatusCode: valueOrEmpty(employment?.code),
      employmentStatusName: valueOrEmpty(employment?.name),
      sectorCode: valueOrEmpty(sector?.code),
      sectorName: valueOrEmpty(sector?.name),
      professionCode: valueOrEmpty(profession?.code),
      professionName: valueOrEmpty(profession?.name),
      employerName: valueOrEmpty(fv.employer),
      employerStreet: buildRoute(fv.premise, fv.streetName),
      employerHouseNumber: valueOrEmpty(fv.houseNumber),
      employerApartment: valueOrEmpty(fv.floor),
      employerCity: valueOrEmpty(fv.cityText),
      employerProvinceCode: valueOrEmpty(province?.code),
      employerProvinceName: valueOrEmpty(province?.name),
      employerPostalCode: valueOrEmpty(fv.postalCode),
      employerCountryCode: countryCode,
      employerLatitude: placeDetails?.latitude ?? null,
      employerLongitude: placeDetails?.longitude ?? null,
      countryJustification: valueOrEmpty(fv.countryJustification)
    };
  }
}
